import type { v2 } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Checkbox,
  ColumnHeader,
  TextField
} from '@deque/cauldron-react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  isUrlValid,
  validateName
} from '~/app/utils/validate-integration-connection-settings';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import { useAxeAccountAnalytics } from '~/common/contexts/analytics';
import { useAuthContext } from '~/common/contexts/auth';
import { useGlobalToast } from '~/common/contexts/globalToast';
import { getIntegrationProductInfoFromSlug } from '~/common/utils/get-integration-product-name-from-slug';
import {
  createKeyPair,
  getIntegrationConnectionById,
  type UpdateBody,
  updateIntegrationConnection
} from '~/common/utils/integrations-client/jira/connections';
import { type ErrorOrNull } from '~/typings/utils';
import styles from './JiraOnPremSettings.css';

export interface JiraOnPremSettingsForm {
  name: string;
  url: string;
  consumerKey: string;
  publicKey: string;
  ignoreSSL: boolean;
}

interface RouteParams {
  id: string;
}

type GenerateNewKeyAlertProps = {
  showAlert: boolean;
  setShowAlert: (showAlert: boolean) => void;
  regenerateKey: () => void;
};

function GenerateNewKeyAlert({
  showAlert,
  setShowAlert,
  regenerateKey
}: GenerateNewKeyAlertProps) {
  const { t } = useTranslation();
  const closeAlert = () => setShowAlert(false);

  if (!showAlert) {
    return null;
  }

  return (
    <Alert
      heading={t('Generate New Key')}
      onClose={closeAlert}
      variant="warning"
      show
    >
      <AlertContent>
        {t(
          'This will immediately generate a new key and permanently overwrite the current one. Are you sure you want to create a new key?'
        )}
      </AlertContent>
      <AlertActions>
        <Button variant="error" onClick={regenerateKey}>
          {t('Create New Key')}
        </Button>
        <Button variant="secondary" onClick={closeAlert}>
          {t('Cancel')}
        </Button>
      </AlertActions>
    </Alert>
  );
}

export interface JiraOnPremSettingsProps {
  activeEnterprise: v2.Enterprise;
}

function JiraOnPremSettings({ activeEnterprise }: JiraOnPremSettingsProps) {
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();
  const analytics = useAxeAccountAnalytics();

  const history = useHistory();
  const { id: connection_id } = useParams<RouteParams>();

  // Get info required for making API calls
  const { user: authUser } = useAuthContext();

  // Get jira on prem settings for this enterprise
  const [jiraOnPremSettingsError, setJiraOnPremSettingsError] =
    useState<ErrorOrNull>(null);
  const [jiraOnPremSettingsLoading, setJiraOnPremSettingsLoading] =
    useState(true);
  const [isConnectionConnected, setIsConnectionConnected] = useState(false);

  useEffect(() => {
    analytics.viewIntegrationsSettingsPage(ProductSlugs.jiraOnPremIntegration);
  }, []);

  useEffect(() => {
    async function fetchJiraOnPremSettings() {
      if (!activeEnterprise || !authUser) {
        return;
      }

      try {
        setJiraOnPremSettingsLoading(true);
        let integrationConnection = await getIntegrationConnectionById({
          enterpriseId: activeEnterprise.id,
          token: authUser.token,
          connectionId: connection_id
        });

        // If there is no key pair, let's create it now
        if (integrationConnection.public_key === null) {
          integrationConnection = await createKeyPair({
            enterpriseId: activeEnterprise.id,
            token: authUser.token,
            connectionId: connection_id
          });
        }

        setIsConnectionConnected(integrationConnection.status === 'connected');

        reset({
          name: integrationConnection.name || '',
          url: integrationConnection.base_url || '',
          consumerKey: integrationConnection.consumer_key || '',
          publicKey: integrationConnection.public_key || '',
          ignoreSSL: integrationConnection.ignore_ssl
        });
      } catch {
        const errorMessage = t('Failed to load settings.');

        setJiraOnPremSettingsError(new Error(errorMessage));
      } finally {
        setJiraOnPremSettingsLoading(false);
      }
    }

    fetchJiraOnPremSettings();
  }, [activeEnterprise, authUser]);

  useEffect(() => {
    if (!jiraOnPremSettingsError) {
      return;
    }

    const toastMessage =
      jiraOnPremSettingsError.message ||
      t('Failed to load settings information.');

    setContents(toastMessage, 'caution');
  }, [jiraOnPremSettingsError]);

  const [showAlert, setShowAlert] = useState(false);

  const { handleSubmit, reset, resetField, control, formState, setValue } =
    useForm<JiraOnPremSettingsForm>();

  const onSubmit = async (data: JiraOnPremSettingsForm) => {
    if (activeEnterprise && authUser) {
      try {
        setJiraOnPremSettingsLoading(true);

        const updates: UpdateBody = {
          name: data.name.trim(),
          ignore_ssl: data.ignoreSSL
        };

        if (!isConnectionConnected) {
          updates.base_url = data.url;
          updates.consumer_key = data.consumerKey;
        }

        await updateIntegrationConnection({
          enterpriseId: activeEnterprise.id,
          connectionId: connection_id,
          token: authUser.token,
          body: updates
        });

        goBackToIntegrationsConfiguration();
      } catch {
        const errorMessage = t('Failed to save settings.');

        setJiraOnPremSettingsError(new Error(errorMessage));
      } finally {
        setJiraOnPremSettingsLoading(false);
      }
    }
  };

  const goBackToIntegrationsConfiguration = () => {
    history.push('/configuration/integrations');
  };
  const resetJiraURL = () => {
    resetField('url');
  };

  const regenerateKey = async (): Promise<void> => {
    setShowAlert(false);

    if (activeEnterprise && authUser && !isConnectionConnected) {
      try {
        setJiraOnPremSettingsLoading(true);

        const integrationConnection = await createKeyPair({
          enterpriseId: activeEnterprise.id,
          connectionId: connection_id,
          token: authUser.token
        });

        setIsConnectionConnected(integrationConnection.status === 'connected');

        setValue('name', integrationConnection.name || '');
        setValue('url', integrationConnection.base_url || '');
        setValue('consumerKey', integrationConnection.consumer_key || '');
        setValue('publicKey', integrationConnection.public_key || '');
        setValue('ignoreSSL', integrationConnection.ignore_ssl);
      } catch {
        const errorMessage = t('Failed to load integration information.');

        setJiraOnPremSettingsError(new Error(errorMessage));
      } finally {
        setJiraOnPremSettingsLoading(false);
      }
    }
  };

  const productName = getIntegrationProductInfoFromSlug(
    ProductSlugs.jiraOnPremIntegration
  ).displayName;
  const productNameHeader = t('{{productName}} Settings', { productName });

  return (
    <>
      <ColumnHeader>
        <h2>{productNameHeader}</h2>
      </ColumnHeader>

      <h3 className={styles.productNameHeader}>{productNameHeader}</h3>

      <GenerateNewKeyAlert
        showAlert={showAlert}
        setShowAlert={setShowAlert}
        regenerateKey={regenerateKey}
      />

      {!jiraOnPremSettingsLoading || (
        <ScrimmedLoader label={t('Loading ...')} />
      )}

      {!jiraOnPremSettingsError && !jiraOnPremSettingsLoading && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={styles.connectionSettingsForm}
        >
          <Controller
            control={control}
            name="name"
            rules={{ validate: (value: string) => validateName(value, t) }}
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                fieldRef={ref}
                label={t('Connection name')}
                required
                requiredText={t('Required')}
                error={formState.errors.name?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="url"
            rules={{ required: true, validate: isUrlValid }}
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                fieldRef={ref}
                label={t('Jira URL')}
                required
                requiredText={t('Required')}
                disabled={isConnectionConnected}
                error={
                  formState.errors.url ? (
                    <div className={styles.errorWithReset}>
                      <Trans>
                        <span>A valid URL is required.</span>
                        <Button
                          id="reset-jira-url"
                          aria-label={t('Reset Jira URL')}
                          variant="link"
                          onClick={resetJiraURL}
                        >
                          Reset
                        </Button>
                      </Trans>
                    </div>
                  ) : undefined
                }
              />
            )}
          />

          <Controller
            control={control}
            name="consumerKey"
            rules={{ required: t('Consumer key is required') }}
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                fieldRef={ref}
                label={t('Consumer Key')}
                required
                requiredText={t('Required')}
                disabled={isConnectionConnected}
                error={formState.errors.consumerKey?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="publicKey"
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                fieldRef={ref}
                label={t('Public Key')}
                disabled
                multiline
              />
            )}
          />

          <Button
            className={styles.generateKey}
            disabled={isConnectionConnected}
            onClick={() => setShowAlert(true)}
          >
            {t('Regenerate Key')}
          </Button>

          <Controller
            control={control}
            name="ignoreSSL"
            render={({ field: { value, ...field } }) => (
              <Checkbox
                {...field}
                id="ignore-ssl-checkbox"
                label={t('Ignore SSL errors')}
                checked={value}
              />
            )}
          />

          <div className={styles.configurationFooter}>
            <Button disabled={false} onClick={handleSubmit(onSubmit)}>
              {t('Save')}
            </Button>
            <Button
              disabled={false}
              onClick={goBackToIntegrationsConfiguration}
              variant="secondary"
            >
              {t('Discard')}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}

export default JiraOnPremSettings;
